import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { useSubscription } from '../hooks/useSubscription';
import { useCancelSubscription } from '../hooks/useCancelSubscription';
import { SubscriptionStatusBadge } from '../components/SubscriptionStatusBadge';
import { SsoHandoffButton } from '../components/SsoHandoffButton';
import { getSubscriptionEvents } from '../services/subscriptions.api';
import { useQuery } from '@tanstack/react-query';

interface SubscriptionDetailPageProps {
  subscriptionId: string;
}

export function SubscriptionDetailPage({ subscriptionId }: SubscriptionDetailPageProps) {
  const { t } = useTranslation();
  const { data: subscription, isLoading, error } = useSubscription(subscriptionId);
  const cancelMutation = useCancelSubscription();

  const { data: events } = useQuery({
    queryKey: ['subscriptions', 'events', subscriptionId],
    queryFn: () => getSubscriptionEvents(subscriptionId),
    enabled: Boolean(subscriptionId),
  });

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelImmediate, setCancelImmediate] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const handleCancel = () => {
    cancelMutation.mutate(
      {
        id: subscriptionId,
        data: { immediate: cancelImmediate, ...(cancelReason && { reason: cancelReason }) },
      },
      {
        onSuccess: () => setShowCancelDialog(false),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="text-center py-20">
        <p className="text-destructive">{t('subscriptions.notFound')}</p>
      </div>
    );
  }

  const canCancel = !['cancelled', 'expired'].includes(subscription.status);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-mono">
            {subscription.id.slice(0, 8)}…
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {t('subscriptions.tenant')}: {subscription.tenant_id}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SubscriptionStatusBadge status={subscription.status} />
          <SsoHandoffButton subscriptionId={subscriptionId} />
          {canCancel && (
            <Button variant="destructive" size="sm" onClick={() => setShowCancelDialog(true)}>
              {t('subscriptions.actions.cancel')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('subscriptions.detail.planId')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-mono text-xs text-foreground">{subscription.plan_id}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('subscriptions.detail.productId')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-mono text-xs text-foreground">{subscription.product_id}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('subscriptions.detail.autoRenew')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground">{subscription.auto_renew ? 'Yes' : 'No'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('subscriptions.detail.started')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground text-sm">
              {new Date(subscription.started_at).toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('subscriptions.detail.currentPeriod')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground text-sm">
              {new Date(subscription.current_period_start).toLocaleDateString()} –{' '}
              {new Date(subscription.current_period_end).toLocaleDateString()}
            </p>
          </CardContent>
        </Card>

        {subscription.trial_ends_at && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('subscriptions.detail.trialEnds')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground text-sm">
                {new Date(subscription.trial_ends_at).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        )}

        {subscription.cancelled_at && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('subscriptions.detail.cancelledAt')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground text-sm">
                {new Date(subscription.cancelled_at).toLocaleString()}
              </p>
              {subscription.cancellation_reason && (
                <p className="text-muted-foreground text-xs mt-1">
                  {subscription.cancellation_reason}
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {events && events.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">
            {t('subscriptions.detail.events')}
          </h2>
          <ol className="relative border-s border-border space-y-4 ms-3">
            {events.map((event) => (
              <li key={event.id} className="ms-6">
                <span className="absolute -start-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 ring-4 ring-background">
                  <span className="h-2 w-2 rounded-full bg-primary" />
                </span>
                <div className="flex items-start gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">{event.event_type}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(event.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {showCancelDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-subscription-title"
            className="bg-card border border-border rounded-xl p-6 shadow-xl max-w-md w-full mx-4 space-y-4"
          >
            <h3 id="cancel-subscription-title" className="text-lg font-semibold text-foreground">
              {t('subscriptions.cancelDialog.title')}
            </h3>

            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={cancelImmediate}
                onChange={(e) => setCancelImmediate(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              {t('subscriptions.cancelDialog.immediate')}
            </label>

            <div className="space-y-1.5">
              <label className="text-sm text-foreground">
                {t('subscriptions.cancelDialog.reason')}
              </label>
              <input
                type="text"
                className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancelDialog(false)}
                disabled={cancelMutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                isLoading={cancelMutation.isPending}
                onClick={handleCancel}
              >
                {t('subscriptions.cancelDialog.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
